import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { getProject, getProjectCalendar } from '../../../../Requests/ProjectRequests';
import { getProjectTasks, getCalendarTasks, getTaskStatuses, getTaskCategories } from '../../../../Requests/TaskRequests';
import { getTeamUuid } from '../../../../utils/cookies';
import { useSidebar } from '../../../../Components/ContextProviders/SidebarContextProvider';
import { getSidebarPadding } from '../../../../utils/sidebar';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/20/solid';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import UpgradedLoader from '../../../../Components/UpgradedLoader';
import TaskForm from '../../../../Components/Tasks/TaskForm';

function Calendar() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { project_uuid } = useParams();
    const { isCollapsed } = useSidebar();
    const [project, setProject] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentView, setCurrentView] = useState('month'); // 'month' or 'week'
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedTask, setSelectedTask] = useState(null);
    const [showTaskForm, setShowTaskForm] = useState(false);
    const [statuses, setStatuses] = useState([]);
    const [categories, setCategories] = useState([]);

    // Get background color from category or default based on type
    const getEventBackgroundColor = (event) => {
        // Use category color if available
        if (event.category && event.category.color) {
            return event.category.color;
        }
        
        // Default colors based on event type
        switch (event.type) {
            case 'task': return '#3B82F6'; // Blue
            case 'event': return '#10B981'; // Green  
            case 'deadline': return '#EF4444'; // Red
            default: return '#6B7280'; // Gray
        }
    };

    // Generate text color based on background (light/dark contrast)
    const getTextColor = (backgroundColor) => {
        // Convert hex to RGB
        let color = backgroundColor.replace('#', '');
        let r = parseInt(color.substring(0, 2), 16);
        let g = parseInt(color.substring(2, 4), 16);
        let b = parseInt(color.substring(4, 6), 16);
        
        // Calculate luminance
        let luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        
        return luminance > 0.5 ? '#000000' : '#FFFFFF';
    };

    // Transform calendar events from API response
    const transformCalendarEvents = (eventsData) => {
        if (!Array.isArray(eventsData)) return [];
        
        return eventsData.map(event => {
            const eventDate = new Date(event.start);
            let timeStr = '';
            
            if (event.allDay) {
                timeStr = event.type === 'deadline' ? 'Due' : 'All Day';
            } else {
                timeStr = eventDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                
                // Add duration info if available
                if (event.duration_minutes) {
                    const hours = Math.floor(event.duration_minutes / 60);
                    const minutes = event.duration_minutes % 60;
                    if (hours > 0) {
                        timeStr += ` (${hours}h${minutes > 0 ? ` ${minutes}m` : ''})`;
                    } else {
                        timeStr += ` (${minutes}m)`;
                    }
                }
            }
            
            const backgroundColor = getEventBackgroundColor(event);
            const textColor = getTextColor(backgroundColor);
            
            return {
                id: `calendar-${event.id}`,
                name: event.title,
                time: timeStr,
                datetime: event.start,
                type: event.type,
                priority: event.priority,
                category: event.category,
                backgroundColor: backgroundColor,
                textColor: textColor,
                task: event,
                video_call_url: event.video_call_url,
                duration_minutes: event.duration_minutes,
                href: '#'
            };
        });
    };

    // Handle task click to open task side view
    const handleTaskClick = (event) => {
        // Get the actual task data from the event
        const task = event.task;
        if (task) {
            setSelectedTask(task);
            setShowTaskForm(true);
        }
    };

    // Handle task update/save
    const handleTaskUpdate = (updatedTask) => {
        // Update the task in events list
        const updatedEvents = events.map(event => {
            if (event.task && event.task.id === updatedTask.id) {
                return {
                    ...event,
                    task: updatedTask,
                    name: updatedTask.title
                };
            }
            return event;
        });
        setEvents(updatedEvents);
        
        // Close the form
        setShowTaskForm(false);
        setSelectedTask(null);
    };

    // Handle task deletion
    const handleTaskDelete = (taskToDelete) => {
        // Remove task from events list
        const updatedEvents = events.filter(event => 
            !(event.task && event.task.id === taskToDelete.id)
        );
        setEvents(updatedEvents);
        
        // Close the form
        setShowTaskForm(false);
        setSelectedTask(null);
    };

    // Handle creating a new task
    const handleCreateTask = () => {
        setSelectedTask(null); // Clear any selected task for new task creation
        setShowTaskForm(true);
    };

    // Handle new task creation/save
    const handleTaskCreate = (newTask) => {
        // Transform the new task to calendar event format
        const newEvent = transformCalendarEvents([{
            id: newTask.id,
            title: newTask.title,
            start: newTask.due_date || newTask.start_date,
            allDay: !newTask.start_time,
            type: 'task',
            priority: newTask.priority,
            category: newTask.category,
            duration_minutes: newTask.duration_minutes || 60
        }]);
        
        // Add the new event to the events list
        if (newEvent.length > 0) {
            setEvents([...events, ...newEvent]);
        }
        
        // Close the form
        setShowTaskForm(false);
        setSelectedTask(null);
    };

    useEffect(() => {
        if (project_uuid) {
            setLoading(true);
            
            // Calculate date range for calendar based on view mode
            let startDate, endDate;
            if (currentView === 'week') {
                // For week view, get the full week (Monday to Sunday)
                const currentWeekStart = new Date(currentDate);
                const dayOfWeek = currentWeekStart.getDay();
                const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
                currentWeekStart.setDate(currentWeekStart.getDate() + daysToMonday);
                
                startDate = new Date(currentWeekStart);
                endDate = new Date(currentWeekStart);
                endDate.setDate(endDate.getDate() + 6); // Sunday of the same week
            } else {
                // For month view, get the full month
                startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
                endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
            }
            
            // Try consolidated calendar API first (reduces 4 API calls to 1)
            const calendarParams = {
                view: currentView,
                start_date: startDate.toISOString().split('T')[0],
                end_date: endDate.toISOString().split('T')[0]
            };
            
            getProjectCalendar(project_uuid, calendarParams).then((response) => {
                // If consolidated endpoint exists, use all the data at once
                const data = response.data;
                setProject(data.project);
                setEvents(transformCalendarEvents(data.events || []));
                setStatuses(data.statuses || []);
                setCategories(data.categories || []);
                
            }).catch((calendarError) => {
                
                // Fallback to individual API calls
                getProject(project_uuid).then((projectResponse) => {
                    const projectData = projectResponse.data;
                    setProject(projectData);
                    
                    // Now load calendar events using team_id and date range
                    // Use team UUID consistently across all APIs
                    let teamUuid = projectData.team_uuid;
                    
                    // If no team_uuid, check if team_id looks like a UUID (contains dashes)
                    if (!teamUuid && projectData.team_id && typeof projectData.team_id === 'string' && projectData.team_id.includes('-')) {
                        teamUuid = projectData.team_id;
                    }
                    
                    // Fallback to current team from cookies if no UUID found
                    if (!teamUuid) {
                        teamUuid = getTeamUuid();
                    }
                    
                    
                    if (!teamUuid) {
                        throw new Error('Team UUID is required for calendar API');
                    }
                    
                    return getCalendarTasks({
                        team_id: teamUuid,
                        start_date: startDate.toISOString().split('T')[0],
                        end_date: endDate.toISOString().split('T')[0]
                    }).then(response => ({ response, startDate, endDate }));
                }).then(({ response: calendarResponse, startDate, endDate }) => {
                    // Handle calendar API response structure
                    const eventsData = calendarResponse.data?.events || [];
                    const calendarEvents = transformCalendarEvents(eventsData);
                    
                    setEvents(calendarEvents);
                    
                }).catch((error) => {
                    console.error('Error loading project or calendar:', error);
                    
                    // Check if it's a 403 error and provide more specific handling
                    if (error.response?.status === 403) {
                        console.error('403 Access denied - Team access issue. Response:', error.response.data);
                        console.error('This may be due to:');
                        console.error('1. User not belonging to the project team');
                        console.error('2. Incorrect team_id being sent');
                        console.error('3. Project team_id/team_uuid mismatch');
                        
                        // Don't navigate away immediately on 403, show error to user
                        alert('Access denied: You may not have permission to view this project calendar. Please check with your team administrator.');
                    } else {
                        navigate('/projects');
                    }
                });
            }).finally(() => {
                setLoading(false);
            });
        }
    }, [project_uuid, navigate, currentDate, currentView]);

    // Load statuses and categories for task form when project is available (fallback for non-consolidated API)
    useEffect(() => {
        if (project && (statuses.length === 0 || categories.length === 0)) {
            // Only run this if consolidated API didn't already populate the data
            
            // Prioritize team UUID, fallback to team_id if it's a UUID format, then cookies
            let teamUuid = project.team_uuid;
            
            // If no team_uuid, check if team_id looks like a UUID (contains dashes)
            if (!teamUuid && project.team_id && typeof project.team_id === 'string' && project.team_id.includes('-')) {
                teamUuid = project.team_id;
            }
            
            // Fallback to current team from cookies if no UUID found in project
            if (!teamUuid) {
                teamUuid = getTeamUuid();
            }
            
            if (teamUuid) {
                // Load both statuses and categories in parallel (only if not already loaded)
                if (statuses.length === 0) {
                    getTaskStatuses(teamUuid).then(statusResponse => {
                        setStatuses(statusResponse.data || []);
                    }).catch(err => {
                        console.warn('Failed to load statuses:', err, 'for team UUID:', teamUuid);
                        setStatuses([]);
                    });
                }
                
                if (categories.length === 0) {
                    getTaskCategories(teamUuid).then(categoryResponse => {
                        setCategories(categoryResponse.data || []);
                    }).catch(err => {
                        console.warn('Failed to load categories:', err, 'for team UUID:', teamUuid);
                        setCategories([]);
                    });
                }
            } else {
                console.warn('No team UUID available for status/category loading. Project:', project);
            }
        }
    }, [project, statuses.length, categories.length]);

    // Generate calendar days for month view
    const generateMonthDays = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const today = new Date();
        
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const firstDayOfMonth = new Date(year, month, 1).getDay();
        const daysInPrevMonth = new Date(year, month, 0).getDate();
        
        const days = [];
        
        // Previous month days
        for (let i = firstDayOfMonth - 1; i >= 0; i--) {
            const day = daysInPrevMonth - i;
            const date = `${year}-${(month).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
            days.push({
                date,
                day,
                isCurrentMonth: false,
                isToday: false,
                isSelected: false,
                events: []
            });
        }
        
        // Current month days
        for (let day = 1; day <= daysInMonth; day++) {
            const date = `${year}-${(month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
            const isToday = today.getDate() === day && today.getMonth() === month && today.getFullYear() === year;
            days.push({
                date,
                day,
                isCurrentMonth: true,
                isToday,
                isSelected: false,
                events: events.filter(event => event.datetime.startsWith(date))
            });
        }
        
        // Next month days to fill the grid
        const remainingCells = 42 - days.length; // 6 rows × 7 days
        for (let day = 1; day <= remainingCells; day++) {
            const date = `${year}-${(month + 2).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
            days.push({
                date,
                day,
                isCurrentMonth: false,
                isToday: false,
                isSelected: false,
                events: []
            });
        }
        
        return days;
    };

    const monthNames = [
        t('Calendar.MonthNames.January'),
        t('Calendar.MonthNames.February'),
        t('Calendar.MonthNames.March'),
        t('Calendar.MonthNames.April'),
        t('Calendar.MonthNames.May'),
        t('Calendar.MonthNames.June'),
        t('Calendar.MonthNames.July'),
        t('Calendar.MonthNames.August'),
        t('Calendar.MonthNames.September'),
        t('Calendar.MonthNames.October'),
        t('Calendar.MonthNames.November'),
        t('Calendar.MonthNames.December')
    ];

    const navigateMonth = (direction) => {
        const newDate = new Date(currentDate);
        newDate.setMonth(currentDate.getMonth() + direction);
        setCurrentDate(newDate);
    };

    const navigateWeek = (direction) => {
        const newDate = new Date(currentDate);
        newDate.setDate(currentDate.getDate() + (direction * 7));
        setCurrentDate(newDate);
    };

    const handleNavigation = (direction) => {
        if (currentView === 'month') {
            navigateMonth(direction);
        } else {
            navigateWeek(direction);
        }
    };

    const goToToday = () => {
        setCurrentDate(new Date());
    };

    const MonthView = () => {
        const days = generateMonthDays();
        
        return (
            <div className="shadow ring-1 ring-black/5 lg:flex lg:flex-auto lg:flex-col h-full">
                <div className="grid grid-cols-7 gap-px border-b border-gray-300 bg-gray-200 text-center text-xs/6 font-semibold text-gray-700 lg:flex-none">
                    <div className="flex justify-center bg-white py-2">
                        <span>{t('Calendar.DayNames.Mon')}</span>
                    </div>
                    <div className="flex justify-center bg-white py-2">
                        <span>{t('Calendar.DayNames.Tue')}</span>
                    </div>
                    <div className="flex justify-center bg-white py-2">
                        <span>{t('Calendar.DayNames.Wed')}</span>
                    </div>
                    <div className="flex justify-center bg-white py-2">
                        <span>{t('Calendar.DayNames.Thu')}</span>
                    </div>
                    <div className="flex justify-center bg-white py-2">
                        <span>{t('Calendar.DayNames.Fri')}</span>
                    </div>
                    <div className="flex justify-center bg-white py-2">
                        <span>{t('Calendar.DayNames.Sat')}</span>
                    </div>
                    <div className="flex justify-center bg-white py-2">
                        <span>{t('Calendar.DayNames.Sun')}</span>
                    </div>
                </div>
                <div className="flex bg-gray-200 text-xs/6 text-gray-700 lg:flex-auto flex-1">
                    <div className="hidden w-full lg:grid lg:grid-cols-7 lg:grid-rows-6 lg:gap-px h-full">
                        {days.map((day, index) => (
                            <div
                                key={`${day.date}-${index}`}
                                data-is-today={day.isToday ? '' : undefined}
                                data-is-current-month={day.isCurrentMonth ? '' : undefined}
                                className="group relative bg-gray-50 px-3 py-3 text-gray-500 data-[is-current-month]:bg-white flex flex-col min-h-[120px]"
                            >
                                <time
                                    dateTime={day.date}
                                    className="relative group-[:not([data-is-current-month])]:opacity-75 [[data-is-today]_&]:flex [[data-is-today]_&]:size-6 [[data-is-today]_&]:items-center [[data-is-today]_&]:justify-center [[data-is-today]_&]:rounded-full [[data-is-today]_&]:bg-iavisus-100 [[data-is-today]_&]:font-semibold [[data-is-today]_&]:text-white"
                                >
                                    {day.day}
                                </time>
                                {day.events.length > 0 ? (
                                    <ol className="mt-2 flex-1 space-y-1">
                                        {day.events.slice(0, 3).map((event) => (
                                            <li key={event.id}>
                                                <div 
                                                    className="group flex flex-col p-2 rounded-md cursor-pointer hover:opacity-80"
                                                    style={{ 
                                                        backgroundColor: event.backgroundColor,
                                                        color: event.textColor
                                                    }}
                                                    onClick={() => handleTaskClick(event)}
                                                >
                                                    <p className="text-xs font-medium truncate">
                                                        {event.name}
                                                    </p>
                                                    <div className="flex items-center justify-between">
                                                        <time
                                                            dateTime={event.datetime}
                                                            className="text-xs opacity-80"
                                                        >
                                                            {event.time}
                                                        </time>
                                                        {event.video_call_url && (
                                                            <a
                                                                href={event.video_call_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-xs opacity-80 hover:opacity-100 underline"
                                                                title="Join video call"
                                                            >
                                                                Join
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                        {day.events.length > 3 ? <li className="text-xs text-gray-500">+ {day.events.length - 3} more</li> : null}
                                    </ol>
                                ) : null}
                            </div>
                        ))}
                    </div>
                    <div className="isolate grid w-full grid-cols-7 grid-rows-6 gap-px lg:hidden h-full">
                        {days.map((day, index) => (
                            <button
                                key={`${day.date}-mobile-${index}`}
                                type="button"
                                data-is-today={day.isToday ? '' : undefined}
                                data-is-selected={day.isSelected ? '' : undefined}
                                data-is-current-month={day.isCurrentMonth ? '' : undefined}
                                className="group relative flex flex-col px-3 py-2 hover:bg-gray-100 focus:z-10 data-[is-current-month]:bg-white data-[is-selected]:font-semibold data-[is-today]:font-semibold data-[is-selected]:text-white data-[is-current-month]:hover:bg-gray-100 [&:not([data-is-current-month])]:bg-gray-50 [&:not([data-is-selected])]:data-[is-today]:text-iavisus-100 [&:not([data-is-selected])]:data-[is-current-month]:[&:not([data-is-today])]:text-gray-900 [&:not([data-is-selected])]:[&:not([data-is-current-month])]:[&:not([data-is-today])]:text-gray-500 min-h-[80px]"
                            >
                                <time
                                    dateTime={day.date}
                                    className="ml-auto group-[:not([data-is-current-month])]:opacity-75 [[data-is-selected]_&]:flex [[data-is-selected]_&]:size-6 [[data-is-selected]_&]:items-center [[data-is-selected]_&]:justify-center [[data-is-selected]_&]:rounded-full [[data-is-selected]_&]:[&:not([data-is-today]_*)]:bg-gray-900 [[data-is-selected]_&]:[[data-is-today]_&]:bg-iavisus-100"
                                >
                                    {day.day}
                                </time>
                                <span className="sr-only">{day.events.length} events</span>
                                {day.events.length > 0 ? (
                                    <span className="-mx-0.5 mt-auto flex flex-wrap-reverse">
                                        {day.events.map((event) => (
                                            <span key={event.id} className="mx-0.5 mb-1 size-1.5 rounded-full bg-gray-400" />
                                        ))}
                                    </span>
                                ) : null}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const WeekView = () => {
        // Calculate the actual week days based on currentDate
        const getWeekDays = () => {
            const today = new Date();
            const currentWeekStart = new Date(currentDate);
            
            // Find Monday of the current week (0 = Sunday, 1 = Monday, etc.)
            const dayOfWeek = currentWeekStart.getDay();
            const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Handle Sunday (0) as last day
            currentWeekStart.setDate(currentWeekStart.getDate() + daysToMonday);
            
            const weekDays = [];
            for (let i = 0; i < 7; i++) {
                const date = new Date(currentWeekStart);
                date.setDate(currentWeekStart.getDate() + i);
                
                // Check if this date is today
                const isToday = today.getDate() === date.getDate() && 
                               today.getMonth() === date.getMonth() && 
                               today.getFullYear() === date.getFullYear();
                
                weekDays.push({
                    day: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
                    fullDay: [
                        t('Calendar.DayNames.Monday'), 
                        t('Calendar.DayNames.Tuesday'), 
                        t('Calendar.DayNames.Wednesday'), 
                        t('Calendar.DayNames.Thursday'), 
                        t('Calendar.DayNames.Friday'), 
                        t('Calendar.DayNames.Saturday'), 
                        t('Calendar.DayNames.Sunday')
                    ][i],
                    date: date.getDate(),
                    fullDate: date.toISOString().split('T')[0],
                    isToday
                });
            }
            return weekDays;
        };
        
        const weekDays = getWeekDays();

        return (
            <div className="isolate flex flex-auto flex-col bg-white h-full">
                <div style={{ width: '165%' }} className="flex max-w-full flex-none flex-col sm:max-w-none md:max-w-full h-full">
                    <div className="sticky top-0 z-30 flex-none bg-white shadow ring-1 ring-black/5 sm:pr-8">
                        <div className="grid grid-cols-7 text-sm/6 text-gray-500 sm:hidden">
                            {weekDays.map((day, index) => (
                                <button key={`${day.day}-${index}`} type="button" className="flex flex-col items-center pb-3 pt-2">
                                    {day.day.charAt(0)}{' '}
                                    <span className={`mt-1 flex size-8 items-center justify-center font-semibold ${
                                        day.isToday 
                                            ? 'rounded-full bg-iavisus-100 text-white' 
                                            : 'text-gray-900'
                                    }`}>
                                        {day.date}
                                    </span>
                                </button>
                            ))}
                        </div>

                        <div className="-mr-px hidden grid-cols-7 divide-x divide-gray-100 border-r border-gray-100 text-sm/6 text-gray-500 sm:grid">
                            <div className="col-end-1 w-14" />
                            {weekDays.map((day, index) => (
                                <div key={`${day.day}-${index}`} className="flex items-center justify-center py-3">
                                    <span className={day.isToday ? "flex items-baseline" : ""}>
                                        {day.day}{' '}
                                        <span className={`items-center justify-center font-semibold ${
                                            day.isToday 
                                                ? 'ml-1.5 flex size-8 items-center justify-center rounded-full bg-iavisus-100 text-white' 
                                                : 'text-gray-900'
                                        }`}>
                                            {day.date}
                                        </span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="flex flex-auto overflow-auto">
                        <div className="sticky left-0 z-10 w-14 flex-none bg-white ring-1 ring-gray-100" />
                        <div className="grid flex-auto grid-cols-1 grid-rows-1 relative">
                            {/* Time grid */}
                            <div
                                style={{ gridTemplateRows: 'repeat(48, minmax(3.5rem, 1fr))' }}
                                className="col-start-1 col-end-2 row-start-1 grid divide-y divide-gray-100"
                            >
                                <div className="row-end-1 h-7" />
                                {Array.from({ length: 24 }, (_, i) => (
                                    <React.Fragment key={i}>
                                        <div>
                                            <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs/5 text-gray-400">
                                                {i === 0 ? '12AM' : i === 12 ? '12PM' : i > 12 ? `${i - 12}PM` : `${i}AM`}
                                            </div>
                                        </div>
                                        <div />
                                    </React.Fragment>
                                ))}
                            </div>

                            {/* Vertical lines */}
                            <div className="col-start-1 col-end-2 row-start-1 hidden grid-rows-1 divide-x divide-gray-100 sm:grid sm:grid-cols-7">
                                {Array.from({ length: 8 }, (_, i) => (
                                    <div key={i} className={`col-start-${i + 1} row-span-full ${i === 7 ? 'w-8' : ''}`} />
                                ))}
                            </div>

                            {/* Week Events */}
                            <ol
                                style={{ gridTemplateRows: 'repeat(48, minmax(3.5rem, 1fr))' }}
                                className="col-start-1 col-end-2 row-start-1 grid grid-cols-1 sm:grid-cols-7 sm:pr-8"
                            >
                                {events
                                    .filter(event => {
                                        // Filter events that fall within the current week
                                        const eventDate = new Date(event.datetime);
                                        const weekStart = new Date(weekDays[0].fullDate);
                                        const weekEnd = new Date(weekDays[6].fullDate);
                                        weekEnd.setHours(23, 59, 59, 999); // End of day
                                        
                                        return eventDate >= weekStart && eventDate <= weekEnd;
                                    })
                                    .map((event, index) => {
                                        // Calculate position in the grid
                                        const eventDate = new Date(event.datetime);
                                        const eventDateStr = event.datetime.split('T')[0];
                                        const dayOfWeek = weekDays.findIndex(day => day.fullDate === eventDateStr);
                                        
                                        const hour = eventDate.getHours();
                                        const minute = eventDate.getMinutes();
                                        
                                        // Calculate grid row (each hour has 2 slots, so 30min = 1 slot)
                                        // Row 1+ = time slots (starting from midnight)
                                        const startRow = 1 + (hour * 2) + Math.floor(minute / 30);
                                        const duration = event.duration_minutes || 60;
                                        const spanRows = Math.max(2, Math.ceil(duration / 30)); // Minimum 60min span (2 rows)
                                        
                                        
                                        return (
                                            <li 
                                                key={event.id} 
                                                style={{ 
                                                    gridRow: `${startRow} / span ${spanRows}`,
                                                    gridColumn: dayOfWeek >= 0 ? dayOfWeek + 1 : 1
                                                }} 
                                                className="relative mt-px flex"
                                            >
                                                <div
                                                    className="group absolute inset-1 flex flex-col overflow-y-auto rounded-lg p-2 text-xs/5 cursor-pointer hover:opacity-80"
                                                    style={{ 
                                                        backgroundColor: event.backgroundColor,
                                                        color: event.textColor
                                                    }}
                                                    onClick={() => handleTaskClick(event)}
                                                >
                                                    <p className="order-1 font-semibold">{event.name}</p>
                                                    <p className="opacity-75">
                                                        <time dateTime={event.datetime}>{event.time}</time>
                                                    </p>
                                                    {event.video_call_url && (
                                                        <a
                                                            href={event.video_call_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="mt-1 text-xs underline opacity-75 hover:opacity-100"
                                                            title="Join video call"
                                                        >
                                                            Join Call
                                                        </a>
                                                    )}
                                                </div>
                                            </li>
                                        );
                                    })}
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (loading || !project) {
        return (
            <div className={`${getSidebarPadding(isCollapsed)} p-6`}>
                <div className="flex items-center justify-center py-12">
                    <UpgradedLoader className="animate-spin" w={8} h={8} bg="bg-black" />
                </div>
            </div>
        );
    }

    return (
        <div className={`${getSidebarPadding(isCollapsed)} transition-all duration-300 pt-4 w-full flex justify-center`} data-testid="calendar-container">
            <div className='flex flex-col w-full justify-center items-center px-4'>
                {/* Navigation Breadcrumb */}
                <div className="mb-6 w-full max-w-7xl">
                    <nav className="flex items-center space-x-2 text-sm text-gray-500">
                        <button
                            onClick={() => navigate('/projects')}
                            className="hover:text-gray-700"
                        >
                            {t('ProjectOverview.AllProjects')}
                        </button>
                        <span>›</span>
                        <button
                            onClick={() => navigate(`/project/${project_uuid}`)}
                            className="hover:text-gray-700"
                        >
                            {project.name}
                        </button>
                        <span>›</span>
                        <span className="text-gray-900 font-medium">{t('Calendar.Title')}</span>
                    </nav>
                </div>

                {/* Calendar Content */}
                <div className="w-full max-w-7xl h-[calc(100vh-200px)]">
                    <div className="lg:flex lg:h-full lg:flex-col h-full">
                <header className="flex items-center justify-between border-b border-gray-200 px-6 py-4 lg:flex-none">
                    <h1 className="text-base font-semibold text-gray-900">
                        <time dateTime={currentDate.toISOString().slice(0, 7)}>
                            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                        </time>
                    </h1>
                    <div className="flex items-center">
                        <div className="relative flex items-center rounded-md bg-white shadow-sm outline outline-1 -outline-offset-1 outline-gray-300 md:items-stretch">
                            <button
                                type="button"
                                onClick={() => handleNavigation(-1)}
                                className="flex h-9 w-12 items-center justify-center rounded-l-md pr-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pr-0 md:hover:bg-gray-50"
                            >
                                <span className="sr-only">{currentView === 'month' ? t('Calendar.PreviousMonth') : t('Calendar.PreviousWeek')}</span>
                                <ChevronLeftIcon aria-hidden="true" className="size-5" />
                            </button>
                            <button
                                type="button"
                                onClick={goToToday}
                                className="hidden px-3.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus:relative md:block"
                            >
                                {currentView === 'month' ? t('Calendar.ThisMonth') : t('Calendar.Today')}
                            </button>
                            <span className="relative -mx-px h-5 w-px bg-gray-300 md:hidden" />
                            <button
                                type="button"
                                onClick={() => handleNavigation(1)}
                                className="flex h-9 w-12 items-center justify-center rounded-r-md pl-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pl-0 md:hover:bg-gray-50"
                            >
                                <span className="sr-only">{currentView === 'month' ? t('Calendar.NextMonth') : t('Calendar.NextWeek')}</span>
                                <ChevronRightIcon aria-hidden="true" className="size-5" />
                            </button>
                        </div>
                        <div className="hidden md:ml-4 md:flex md:items-center">
                            <Menu as="div" className="relative">
                                <MenuButton
                                    type="button"
                                    className="flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                >
                                    {currentView === 'month' ? t('Calendar.MonthView') : t('Calendar.WeekView')}
                                    <ChevronDownIcon aria-hidden="true" className="-mr-1 size-5 text-gray-400" />
                                </MenuButton>

                                <MenuItems
                                    transition
                                    className="absolute right-0 z-10 mt-3 w-36 origin-top-right overflow-hidden rounded-md bg-white shadow-lg outline outline-1 outline-black/5 transition data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                                >
                                    <div className="py-1">
                                        <MenuItem>
                                            <button
                                                onClick={() => setCurrentView('week')}
                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"
                                            >
                                                {t('Calendar.WeekView')}
                                            </button>
                                        </MenuItem>
                                        <MenuItem>
                                            <button
                                                onClick={() => setCurrentView('month')}
                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"
                                            >
                                                {t('Calendar.MonthView')}
                                            </button>
                                        </MenuItem>
                                    </div>
                                </MenuItems>
                            </Menu>
                            <div className="ml-6 h-6 w-px bg-gray-300" />
                            <button
                                type="button"
                                onClick={handleCreateTask}
                                className="ml-6 rounded-md bg-iavisus-100 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-iavisus-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-iavisus-100"
                            >
                                {t('Tasks.AddTask')}
                            </button>
                        </div>
                        <Menu as="div" className="relative ml-6 md:hidden">
                            <MenuButton className="-mx-2 flex items-center rounded-full border border-transparent p-2 text-gray-400 hover:text-gray-500">
                                <span className="sr-only">{t('Calendar.OpenMenu')}</span>
                                <EllipsisHorizontalIcon aria-hidden="true" className="size-5" />
                            </MenuButton>

                            <MenuItems
                                transition
                                className="absolute right-0 z-10 mt-3 w-36 origin-top-right divide-y divide-gray-100 overflow-hidden rounded-md bg-white shadow-lg outline outline-1 outline-black/5 transition data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                            >
                                <div className="py-1">
                                    <MenuItem>
                                        <button
                                            onClick={handleCreateTask}
                                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"
                                        >
                                            {t('Tasks.CreateTask')}
                                        </button>
                                    </MenuItem>
                                </div>
                                <div className="py-1">
                                    <MenuItem>
                                        <button
                                            onClick={goToToday}
                                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"
                                        >
                                            {t('Calendar.GoToToday')}
                                        </button>
                                    </MenuItem>
                                </div>
                                <div className="py-1">
                                    <MenuItem>
                                        <button
                                            onClick={() => setCurrentView('week')}
                                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"
                                        >
                                            {t('Calendar.WeekView')}
                                        </button>
                                    </MenuItem>
                                    <MenuItem>
                                        <button
                                            onClick={() => setCurrentView('month')}
                                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"
                                        >
                                            {t('Calendar.MonthView')}
                                        </button>
                                    </MenuItem>
                                </div>
                            </MenuItems>
                        </Menu>
                    </div>
                </header>

                {/* Calendar Content */}
                {currentView === 'month' ? <MonthView /> : <WeekView />}

                        {/* Mobile Events List */}
                        {currentView === 'month' && (
                            <div className="relative px-4 py-10 sm:px-6 lg:hidden">
                                <ol className="divide-y divide-gray-100 overflow-hidden rounded-lg bg-white text-sm shadow outline outline-1 outline-black/5">
                                    {events.map((event) => (
                                        <li key={event.id} className="group flex p-4 pr-6 focus-within:bg-gray-50 hover:bg-gray-50 cursor-pointer" onClick={() => handleTaskClick(event)}>
                                            <div 
                                                className="flex-none w-3 h-3 rounded-full mr-3 mt-1"
                                                style={{ backgroundColor: event.backgroundColor }}
                                            />
                                            <div className="flex-auto">
                                                <p className="font-semibold text-gray-900">{event.name}</p>
                                                <time dateTime={event.datetime} className="mt-2 flex items-center text-gray-700">
                                                    <ClockIcon aria-hidden="true" className="mr-2 size-5 text-gray-400" />
                                                    {event.time}
                                                </time>
                                            </div>
                                            <div className="ml-6 flex items-center space-x-2">
                                                {event.video_call_url && (
                                                    <a
                                                        href={event.video_call_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex-none self-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 opacity-0 focus:opacity-100 group-hover:opacity-100"
                                                        title="Join video call"
                                                    >
                                                        Join Call
                                                    </a>
                                                )}
                                                <a
                                                    href={event.href}
                                                    className="flex-none self-center rounded-md bg-white px-3 py-2 font-semibold text-gray-900 opacity-0 shadow-sm ring-1 ring-inset ring-gray-300 hover:ring-gray-400 focus:opacity-100 group-hover:opacity-100"
                                                >
                                                    {t('Calendar.Edit')}<span className="sr-only">, {event.name}</span>
                                                </a>
                                            </div>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Task Form Drawer */}
            {showTaskForm && project && (
                <TaskForm
                    open={showTaskForm}
                    task={selectedTask}
                    project={project}
                    statuses={statuses}
                    categories={categories}
                    onSave={selectedTask ? handleTaskUpdate : handleTaskCreate}
                    onDelete={handleTaskDelete}
                    onCancel={() => {
                        setShowTaskForm(false);
                        setSelectedTask(null);
                    }}
                />
            )}
        </div>
    );
}

export default Calendar;